import { db } from './index';
import { queueRepo } from './queue';

function nowIso() {
  return new Date().toISOString();
}

function checkinId(habitId, date) {
  return `${habitId}#${date}`;
}

export const checkinsRepo = {
  async listForHabit(userId, habitId) {
    const rows = await db.checkins
      .where('habitId')
      .equals(habitId)
      .filter((c) => c.userId === userId && !c.deletedAt)
      .toArray();
    return rows.map((c) => c.date).sort();
  },

  async listAllForUser(userId) {
    return db.checkins
      .where('userId')
      .equals(userId)
      .filter((c) => !c.deletedAt)
      .toArray();
  },

  async existsForDate(userId, habitId, date) {
    const row = await db.checkins.get(checkinId(habitId, date));
    return !!(row && row.userId === userId && !row.deletedAt);
  },

  async create(userId, habitId, date) {
    const now = nowIso();
    const id = checkinId(habitId, date);
    const existing = await db.checkins.get(id);
    if (existing && !existing.deletedAt) {
      // Idempotent — already checked in.
      return existing;
    }
    const entry = {
      id,
      habitId,
      userId,
      date,
      createdAt: now,
      syncStatus: 'local',
      localUpdatedAt: now,
      deletedAt: null,
    };
    await db.checkins.put(entry);
    await queueRepo.enqueue({
      entity: 'checkin',
      op: 'create',
      recordId: id,
      payload: { habitId, date },
    });
    return entry;
  },

  async softDelete(userId, habitId, date) {
    const id = checkinId(habitId, date);
    const existing = await db.checkins.get(id);
    if (!existing || existing.userId !== userId) return;
    const now = nowIso();
    await db.checkins.put({
      ...existing,
      deletedAt: now,
      syncStatus: 'local',
      localUpdatedAt: now,
    });
    await queueRepo.enqueue({
      entity: 'checkin',
      op: 'delete',
      recordId: id,
      payload: { habitId, date },
    });
  },

  async hardDelete(id) {
    await db.checkins.delete(id);
  },

  async upsertFromServer(userId, habitId, date) {
    const id = checkinId(habitId, date);
    const existing = await db.checkins.get(id);
    if (existing && existing.syncStatus === 'local') {
      // Local edit in flight; let push reconcile.
      return;
    }
    await db.checkins.put({
      id,
      habitId,
      userId,
      date,
      syncStatus: 'synced',
      localUpdatedAt: new Date().toISOString(),
      deletedAt: null,
    });
  },

  async clearForUser(userId) {
    await db.checkins.where('userId').equals(userId).delete();
  },

  async replaceServerSetForHabit(userId, habitId, serverDates) {
    // After a server pull for one habit, reconcile its checkin set:
    //   - add anything new from server
    //   - remove anything we have locally as 'synced' that is no longer present
    //   - never touch rows with syncStatus === 'local' (push will reconcile)
    const serverSet = new Set(serverDates);
    const local = await db.checkins.where('habitId').equals(habitId).toArray();
    for (const row of local) {
      if (row.userId !== userId) continue;
      if (row.syncStatus === 'local') continue;
      if (!serverSet.has(row.date)) {
        await db.checkins.delete(row.id);
      }
    }
    for (const date of serverDates) {
      await this.upsertFromServer(userId, habitId, date);
    }
  },
};
